// The message catalog. One flat, dotted-key `Messages` shape; the three locale
// catalogs are each typed `Messages`, so TypeScript fails the build if any
// locale is missing a key or has a stray one — locale parity is enforced at
// compile time, not left to a runtime check.
//
// Copy source of truth: bfc-command-center/docs/specs/bfc/website-content{,.es,.pt}.md
// (the approved trilingual marketing copy). Generic UI strings (form, nav,
// switcher) are translated in the same neutral, professional register.

export type Locale = 'en' | 'es' | 'pt-BR';

// Display order of the switcher.
export const LOCALES: readonly Locale[] = ['en', 'es', 'pt-BR'];

export interface Messages {
  // SEO / <head> metadata (#206) — rendered per-locale into <title>, the meta
  // description, and Open Graph title/description by i18n/locale.ts on locale
  // resolution and change, so search snippets + social cards match the locale.
  'seo.title': string;
  'seo.description': string;
  'seo.ogDescription': string;
  'seo.route.contact': string;
  'seo.route.privacy': string;
  'seo.route.login': string;
  'seo.route.leads': string;
  'seo.route.reports': string;
  'seo.route.users': string;
  'seo.route.console': string;
  'seo.route.devices': string;
  'seo.route.account': string;
  // Home — hero
  'home.eyebrow': string;
  'home.lead': string;
  'home.sub': string;
  'home.cta': string;
  'home.cta2': string;
  // Home — highlight cards
  'home.card1.title': string;
  'home.card1.body': string;
  'home.card2.title': string;
  'home.card2.body': string;
  'home.card3.title': string;
  'home.card3.body': string;
  // Section — Who We Are
  'who.title': string;
  'who.p1': string;
  'who.p2': string;
  'who.p3': string;
  // Section — What We Do
  'what.title': string;
  'what.intro': string;
  'what.sourcing.title': string;
  'what.sourcing.body': string;
  'what.logistics.title': string;
  'what.logistics.body': string;
  'what.expertise.title': string;
  'what.expertise.body': string;
  // Section — Where We Operate
  'where.title': string;
  'where.intro': string;
  'where.us.name': string;
  'where.us.desc': string;
  'where.mx.name': string;
  'where.mx.desc': string;
  'where.sa.name': string;
  'where.sa.desc': string;
  'where.outro': string;
  // Section — What We Supply
  'supply.title': string;
  'supply.intro': string;
  'supply.core.title': string;
  'supply.core.btx.name': string;
  'supply.core.btx.desc': string;
  'supply.core.olefins.name': string;
  'supply.core.olefins.desc': string;
  'supply.core.solvents.name': string;
  'supply.core.solvents.desc': string;
  'supply.core.fuel.name': string;
  'supply.core.fuel.desc': string;
  'supply.refining.title': string;
  'supply.refining.catalysts.name': string;
  'supply.refining.catalysts.desc': string;
  'supply.refining.hydro.name': string;
  'supply.refining.hydro.desc': string;
  'supply.refining.process.name': string;
  'supply.refining.process.desc': string;
  'supply.specialty.title': string;
  'supply.specialty.additives.name': string;
  'supply.specialty.additives.desc': string;
  'supply.specialty.coatings.name': string;
  'supply.specialty.coatings.desc': string;
  'supply.specialty.custom.name': string;
  'supply.specialty.custom.desc': string;
  'supply.outro': string;
  // Section — Trusted Supply Relationships
  'partners.title': string;
  'partners.intro': string;
  'partners.basf': string;
  'partners.3m': string;
  'partners.honeywell': string;
  'partners.marcopolo': string;
  'partners.outro': string;
  // Section — Industries We Serve
  'industries.title': string;
  'industries.intro': string;
  'industries.oilgas': string;
  'industries.refiners': string;
  'industries.manufacturers': string;
  'industries.energy': string;
  'industries.government': string;
  'industries.outro': string;
  // Section — Logistics & Delivery
  'logistics.title': string;
  'logistics.intro': string;
  'logistics.formats.title': string;
  'logistics.formats.desc': string;
  'logistics.terminal.title': string;
  'logistics.terminal.desc': string;
  'logistics.compliance.title': string;
  'logistics.compliance.desc': string;
  'logistics.terms.title': string;
  'logistics.terms.desc': string;
  'logistics.rapid.title': string;
  'logistics.rapid.desc': string;
  'logistics.outro': string;
  'logistics.note': string;
  // Section — Why Bluefield Chemicals
  'why.title': string;
  'why.supply.title': string;
  'why.supply.body': string;
  'why.speed.title': string;
  'why.speed.body': string;
  'why.rules.title': string;
  'why.rules.body': string;
  'why.practical.title': string;
  'why.practical.body': string;
  'why.protect.title': string;
  'why.protect.body': string;
  // Section — For Buyers
  'buyers.title': string;
  'buyers.intro': string;
  'buyers.pain1': string;
  'buyers.pain2': string;
  'buyers.pain3': string;
  'buyers.pain4': string;
  'buyers.outro': string;
  'buyers.cta': string;
  // Section — Contact (inline)
  'info.title': string;
  'info.intro': string;
  'info.sales': string;
  'info.procurement': string;
  'info.trading': string;
  'info.phone': string;
  'info.offices': string;
  'info.officesValue': string;
  // Section — Closing
  'closing.statement': string;
  // Nav — section anchors
  'nav.about': string;
  'nav.supply': string;
  'nav.why': string;
  // Nav — existing
  'nav.home': string;
  'nav.contact': string;
  'nav.me': string;
  'nav.devices': string;
  'nav.passkeys': string;
  'nav.admin': string;
  'nav.users': string;
  'nav.capture': string;
  'nav.myLeads': string;
  'nav.leadsInbox': string;
  'nav.inbox': string;
  'nav.leads': string;
  'nav.login': string;
  'nav.account': string;
  'nav.console': string;
  'nav.reports': string;
  // Contact — intro
  'contact.title': string;
  'contact.sub': string;
  // Contact — form fields
  'contact.field.name': string;
  'contact.field.email': string;
  'contact.field.company': string;
  'contact.field.need': string;
  'contact.field.message': string;
  'contact.field.reason': string;
  'contact.consent': string;
  'contact.submit': string;
  'contact.sending': string;
  // Contact — states
  'contact.validation': string;
  'contact.requiredHint': string;
  'contact.err.required': string;
  'contact.err.email': string;
  'contact.err.consent': string;
  'contact.error.badRequest': string;
  'contact.error.forbidden': string;
  'contact.error.rateLimited': string;
  'contact.error.server': string;
  'contact.error.network': string;
  'contact.success.title': string;
  'contact.success.lead': string;
  'contact.consentLink': string;
  // Field capture (RAD §8) — the signed-in rep's phone-first lead form
  'capture.title': string;
  'capture.sub': string;
  'capture.field.firstName': string;
  'capture.field.lastName': string;
  'capture.field.email': string;
  'capture.field.cell': string;
  'capture.field.company': string;
  'capture.field.product': string;
  'capture.field.amount': string;
  'capture.field.notes': string;
  'capture.consent': string;
  'capture.consentLink': string;
  'capture.submit': string;
  'capture.sending': string;
  'capture.validation': string;
  'capture.error.badRequest': string;
  'capture.error.forbidden': string;
  'capture.error.server': string;
  'capture.error.network': string;
  'capture.success.title': string;
  'capture.success.lead': string;
  'capture.offline.title': string;
  'capture.offline.lead': string;
  'capture.pending': string;
  'capture.another': string;
  // My leads (RAD §8) — the rep's list of their own captured leads
  'leads.title': string;
  'leads.sub': string;
  'leads.search': string;
  'leads.count.one': string;
  'leads.count.other': string;
  'leads.loading': string;
  'leads.empty': string;
  'leads.noMatch': string;
  'leads.error': string;
  'leads.retry': string;
  'leads.more': string;
  'leads.col.name': string;
  'leads.col.company': string;
  'leads.col.product': string;
  'leads.col.source': string;
  'leads.col.status': string;
  'leads.col.assignee': string;
  'leads.col.captured': string;
  'leads.status.new': string;
  'leads.status.contacted': string;
  'leads.status.qualified': string;
  'leads.status.quoted': string;
  'leads.status.won': string;
  'leads.status.lost': string;
  'leads.source.web': string;
  'leads.source.field': string;
  'leads.inbox.title': string;
  'leads.inbox.sub': string;
  'leads.inbox.search': string;
  'leads.inbox.loading': string;
  'leads.inbox.empty': string;
  'leads.inbox.emptyFiltered': string;
  'leads.inbox.error': string;
  'leads.filter.source': string;
  'leads.filter.status': string;
  'leads.filter.assignee': string;
  'leads.filter.unassigned': string;
  'leads.filter.all': string;
  // CSV export (RAD §9, #36) — desktop-only, capability-gated toolbar action
  'leads.export.action': string;
  'leads.export.busy': string;
  'leads.export.error': string;
  'leads.export.aria': string;
  'leads.export.none': string;
  // Lead detail + notes + activity timeline (RAD §9)
  'leads.detail.open': string;
  'leads.detail.back': string;
  'leads.detail.loading': string;
  'leads.detail.error': string;
  'leads.detail.notFound': string;
  'leads.detail.record': string;
  'leads.detail.activity': string;
  'leads.detail.amount': string;
  'leads.detail.email': string;
  'leads.detail.phone': string;
  'leads.detail.owner': string;
  'leads.detail.capturedBy': string;
  'leads.detail.message': string;
  'leads.detail.notes': string;
  'leads.detail.consent': string;
  'leads.detail.consentYes': string;
  'leads.detail.consentNo': string;
  'leads.detail.created': string;
  'leads.detail.lastActivity': string;
  'leads.detail.unassigned': string;
  'leads.activity.empty': string;
  'leads.activity.created': string;
  'leads.activity.note': string;
  'leads.activity.statusChanged': string;
  'leads.activity.assigned': string;
  'leads.note.label': string;
  'leads.note.placeholder': string;
  'leads.note.add': string;
  'leads.note.adding': string;
  'leads.note.empty': string;
  'leads.note.error': string;
  'leads.status.change': string;
  'leads.status.updating': string;
  'leads.status.error': string;
  'leads.status.conflict': string;
  'leads.assign.label': string;
  'leads.assign.placeholder': string;
  'leads.assign.action': string;
  'leads.assign.empty': string;
  'leads.assign.assigning': string;
  'leads.assign.done': string;
  'leads.assign.conflict': string;
  'leads.assign.invalid': string;
  'leads.assign.error': string;
  // Footer
  'footer.privacy': string;
  // Privacy & cookies page (full policy — issue #46)
  'privacy.title': string;
  'privacy.updated': string;
  'privacy.intro': string;
  'privacy.who.title': string;
  'privacy.who.body': string;
  'privacy.collect.title': string;
  'privacy.collect.body': string;
  'privacy.use.title': string;
  'privacy.use.body': string;
  'privacy.store.title': string;
  'privacy.store.body': string;
  'privacy.retain.title': string;
  'privacy.retain.body': string;
  'privacy.rights.title': string;
  'privacy.rights.body': string;
  'privacy.cookies.title': string;
  'privacy.cookies.body': string;
  'privacy.requests.title': string;
  'privacy.requests.body': string;
  'privacy.requests.cta': string;
  // Locale switcher
  'locale.switcher.label': string;
  'locale.name.en': string;
  'locale.name.es': string;
  'locale.name.pt': string;
  // Admin — user management (#78)
  'admin.users.title': string;
  'admin.users.sub': string;
  'admin.users.field.email': string;
  'admin.users.field.name': string;
  'admin.users.field.role': string;
  'admin.users.role.placeholder': string;
  'admin.users.submit': string;
  'admin.users.success': string;
  'admin.users.error.email': string;
  'admin.users.error.exists': string;
  'admin.users.error.role': string;
  'admin.users.error.badRequest': string;
  'admin.users.error.forbidden': string;
  'admin.users.error.server': string;
  'admin.users.error.network': string;
  'admin.role.admin': string;
  'admin.role.manager': string;
  'admin.role.sales': string;
  'admin.role.viewer': string;
  'admin.desktop.title': string;
  'admin.desktop.copy': string;
  // Admin — visibility console (#37, RAD §10)
  'console.title': string;
  'console.sub': string;
  'console.loading': string;
  'console.lens.status': string;
  'console.lens.security': string;
  'console.lens.activity': string;
  'console.lens.compliance': string;
  'console.lens.config': string;
  'console.desktop.title': string;
  'console.desktop.copy': string;
  'console.error': string;
  'console.retry': string;
  // System-status lens (#38, RAD §10)
  'console.status.error': string;
  'console.status.retry': string;
  'console.status.deploy.title': string;
  'console.status.deploy.version': string;
  'console.status.deploy.commit': string;
  'console.status.deploy.built': string;
  'console.status.db.title': string;
  'console.status.db.ok': string;
  'console.status.db.down': string;
  'console.status.db.latency': string;
  'console.status.traffic.title': string;
  'console.status.traffic.window': string;
  'console.status.traffic.unavailable': string;
  'console.status.traffic.requests': string;
  'console.status.traffic.errorRate': string;
  'console.status.traffic.clientErrors': string;
  'console.status.traffic.serverErrors': string;
  'console.status.traffic.topEndpoints': string;
  'console.status.traffic.noEndpoints': string;
  'console.security.error': string;
  'console.security.denials.title': string;
  'console.security.denials.window': string;
  'console.security.denials.unavailable': string;
  'console.security.denials.total': string;
  'console.security.denials.byReason': string;
  'console.security.denials.topSources': string;
  'console.security.denials.none': string;
  'console.security.blocks.title': string;
  'console.security.blocks.none': string;
  'console.security.blocks.reason': string;
  'console.security.blocks.blockedBy': string;
  'console.security.blocks.blockedAt': string;
  'console.security.blocks.expires': string;
  'console.security.blocks.permanent': string;
  'console.security.blocks.unblock': string;
  'console.security.tiers.title': string;
  'console.security.tiers.none': string;
  'console.security.tiers.tier': string;
  'console.security.tiers.limit': string;
  'console.security.unblock.title': string;
  'console.security.unblock.prompt': string;
  'console.security.unblock.reason': string;
  'console.security.unblock.placeholder': string;
  'console.security.unblock.confirm': string;
  'console.security.unblock.cancel': string;
  'console.security.unblock.submitting': string;
  'console.security.unblock.error': string;
  'console.security.unblock.required': string;
  'console.activity.error': string;
  'console.activity.audit.title': string;
  'console.activity.audit.sub': string;
  'console.activity.audit.empty': string;
  'console.activity.filter.outcome': string;
  'console.activity.filter.all': string;
  'console.activity.outcome.ok': string;
  'console.activity.outcome.client_error': string;
  'console.activity.outcome.server_error': string;
  'console.activity.filter.actor': string;
  'console.activity.filter.clear': string;
  'console.activity.col.time': string;
  'console.activity.col.actor': string;
  'console.activity.col.action': string;
  'console.activity.col.source': string;
  'console.activity.col.outcome': string;
  'console.activity.actor.none': string;
  'console.activity.more': string;
  'console.activity.signins.title': string;
  'console.activity.signins.sub': string;
  'console.activity.signins.empty': string;
  'console.activity.event.issued': string;
  'console.activity.event.refreshed': string;
  'console.activity.event.revoked': string;
  'console.activity.event.revoked_all': string;
  // Compliance lens (#44, RAD §12)
  'console.compliance.error': string;
  'console.compliance.summary.title': string;
  'console.compliance.summary.sub': string;
  'console.compliance.total': string;
  'console.compliance.action.export': string;
  'console.compliance.action.export_csv': string;
  'console.compliance.action.rectify': string;
  'console.compliance.action.erase': string;
  'console.compliance.recent.title': string;
  'console.compliance.recent.empty': string;
  'console.compliance.recent.col.time': string;
  'console.compliance.recent.col.actor': string;
  'console.compliance.recent.col.action': string;
  'console.compliance.recent.col.affected': string;
  'console.compliance.recent.actor.none': string;
  'console.compliance.export.title': string;
  'console.compliance.export.sub': string;
  'console.compliance.export.emailLabel': string;
  'console.compliance.export.emailPlaceholder': string;
  'console.compliance.export.button': string;
  'console.compliance.export.busy': string;
  'console.compliance.export.success': string;
  'console.compliance.export.error': string;
  'console.compliance.export.invalidEmail': string;
  // Auth / account / settings cluster (#153) — shared
  'auth.email': string;
  'auth.emailPlaceholder': string;
  'auth.sending': string;
  'auth.cancel': string;
  'auth.backToSignIn': string;
  'auth.error.network': string;
  'auth.error.rateLimited': string;
  'auth.error.server': string;
  // Login page (#153)
  'login.title': string;
  'login.sub': string;
  'login.submit': string;
  'login.sent': string;
  'login.success': string;
  'login.footnote': string;
  'login.or': string;
  'login.oauth.apple': string;
  'login.oauth.google': string;
  'login.recoveryLink': string;
  'login.error.captcha': string;
  // OAuth callback errors (services/auth.ts, #153)
  'login.oauthError.denied': string;
  'login.oauthError.emailMissing': string;
  'login.oauthError.emailUnverified': string;
  'login.oauthError.deviceLimit': string;
  'login.oauthError.stateInvalid': string;
  'login.oauthError.generic': string;
  // Recovery page (#153)
  'recovery.title': string;
  'recovery.sub': string;
  'recovery.step1.title': string;
  'recovery.step1.detail': string;
  'recovery.step2.title': string;
  'recovery.step2.detail': string;
  'recovery.step3.title': string;
  'recovery.step3.detail': string;
  'recovery.submit': string;
  'recovery.success.title': string;
  'recovery.success.lead': string;
  'recovery.success.note': string;
  'recovery.error.rateLimited': string;
  // Auth-verify page (#153)
  'verify.title': string;
  'verify.sub': string;
  'verify.deviceLimit.title': string;
  'verify.deviceLimit.capN': string;
  'verify.deviceLimit.capUnknown': string;
  'verify.deviceLimit.message': string;
  'verify.deviceLimit.cta': string;
  'verify.invalid.title': string;
  'verify.invalid.expired': string;
  'verify.invalid.generic': string;
  'verify.connection.title': string;
  'verify.connection.message': string;
  'verify.connection.retry': string;
  // Devices & sessions page (#153)
  'sessions.title': string;
  'sessions.sub': string;
  'sessions.loading': string;
  'sessions.empty': string;
  'sessions.retry': string;
  'sessions.error.load': string;
  'sessions.error.alreadyOut': string;
  'sessions.error.currentDevice': string;
  'sessions.error.generic': string;
  'sessions.thisDevice': string;
  'sessions.col.device': string;
  'sessions.col.lastUsed': string;
  'sessions.col.ip': string;
  'sessions.col.signedIn': string;
  'sessions.actions': string;
  'sessions.reported': string;
  'sessions.signOutThis': string;
  'sessions.revoke': string;
  'sessions.revokeAria': string;
  'sessions.reportBtn': string;
  'sessions.reportAria': string;
  'sessions.signOutOthers': string;
  'sessions.unknown': string;
  'sessions.unknownDevice': string;
  'sessions.lastUsed.activeNow': string;
  'sessions.lastUsed.minuteOne': string;
  'sessions.lastUsed.minuteOther': string;
  'sessions.lastUsed.hourOne': string;
  'sessions.lastUsed.hourOther': string;
  'sessions.lastUsed.yesterday': string;
  'sessions.lastUsed.dayOther': string;
  'sessions.confirmSignOut.title': string;
  'sessions.confirmSignOut.message': string;
  'sessions.confirmSignOut.confirm': string;
  'sessions.confirmSignOut.cancel': string;
  'sessions.confirmReport.title': string;
  'sessions.confirmReport.message': string;
  'sessions.confirmReport.confirm': string;
  'sessions.confirmSignOutOthers.title': string;
  'sessions.confirmSignOutOthers.messageOne': string;
  'sessions.confirmSignOutOthers.messageOther': string;
  'sessions.confirmSignOutOthers.confirm': string;
  'sessions.status.signedOut': string;
  'sessions.status.reported': string;
  'sessions.status.signedOutOthers.one': string;
  'sessions.status.signedOutOthers.other': string;
  'sessions.status.signedOutPartial.one': string;
  'sessions.status.signedOutPartial.other': string;
  // Passkeys page (#153)
  'passkeys.title': string;
  'passkeys.sub': string;
  'passkeys.loading': string;
  'passkeys.unsupported': string;
  'passkeys.retry': string;
  'passkeys.error.load': string;
  'passkeys.error.notAllowed': string;
  'passkeys.error.invalidState': string;
  'passkeys.error.setup': string;
  'passkeys.error.forbidden': string;
  'passkeys.error.generic': string;
  'passkeys.error.remove': string;
  'passkeys.empty': string;
  'passkeys.label': string;
  'passkeys.labelLower': string;
  'passkeys.unknown': string;
  'passkeys.never': string;
  'passkeys.meta.added': string;
  'passkeys.meta.lastUsed': string;
  'passkeys.add.first': string;
  'passkeys.add.another': string;
  'passkeys.testStepUp': string;
  'passkeys.verify.success': string;
  'passkeys.verify.cancelled': string;
  'passkeys.verify.failed': string;
  'passkeys.remove': string;
  'passkeys.removeAria': string;
  'passkeys.added': string;
  'passkeys.removed': string;
  'passkeys.removed.already': string;
  'passkeys.confirmRemove.title': string;
  'passkeys.confirmRemove.message': string;
  'passkeys.confirmRemove.messageLast': string;
  'passkeys.confirmRemove.cancel': string;
  'passkeys.info.sub': string;
  // Me / account hub (#153)
  'me.account': string;
  'me.appearance': string;
  'me.signOut': string;
  'me.theme.label': string;
  'me.theme.auto': string;
  'me.theme.light': string;
  'me.theme.dark': string;
  'me.signedIn.phone': string;
  'me.signedIn.tablet': string;
  'me.signedIn.desktop': string;
  'me.signedIn.generic': string;
  'me.confirmSignOut.title': string;
  'me.confirmSignOut.message': string;
  // Error screen (#153)
  'error.forbidden.title': string;
  'error.forbidden.copy': string;
  'error.notFound.title': string;
  'error.notFound.copy': string;
  'error.offline.title': string;
  'error.offline.copy': string;
  'error.generic.title': string;
  'error.generic.copy': string;
  'error.action.home': string;
  'error.action.retry': string;
  // Passkey step-up / enroll modal (#153)
  'passkey.desktop.title': string;
  'passkey.desktop.message': string;
  'passkey.desktop.dismiss': string;
  'passkey.verify.title': string;
  'passkey.verify.message': string;
  'passkey.verify.action': string;
  'passkey.verify.progress': string;
  'passkey.enroll.title': string;
  'passkey.enroll.message': string;
  'passkey.enroll.action': string;
  'passkey.enroll.progress': string;
  'passkey.enroll.cancel': string;
  'passkey.error.cancelled': string;
  'passkey.error.exists': string;
  'passkey.error.authenticator': string;
  'passkey.error.verifyFailed': string;
  'passkey.error.generic': string;
  // Health pill (#153)
  'health.status': string;
  'health.checking': string;
  'health.online': string;
  'health.degraded': string;
  'health.offline': string;
  // Health-pill detail tooltip + build stamp (#261). {status}/{latency} are the
  // Couchbase probe result; {time} is the build timestamp.
  'health.detail': string;
  'build.builtAt': string;
  // Shared component chrome — data table, form, confirm dialog, nav (#154)
  'table.aria': string;
  'table.hint': string;
  'table.hint.menu': string;
  'table.filter': string;
  'table.search': string;
  'table.empty': string;
  'table.detailEmpty': string;
  'table.unit.one': string;
  'table.unit.other': string;
  'form.save': string;
  'form.select': string;
  'form.required': string;
  'confirm.cancel': string;
  'confirm.confirm': string;
  'nav.primaryAria': string;
  'nav.skipToContent': string;
}

const en: Messages = {
  'seo.title': 'Bluefield Chemicals — Petrochemical Sourcing & Trading',
  'seo.description':
    'Bluefield Chemicals is a petrochemical sourcing and trading partner for companies that cannot afford to run short — across the United States, Mexico, and South America.',
  'seo.ogDescription':
    'Petrochemical sourcing and trading partner — securing supply, moving markets, delivering on every commitment.',
  'seo.route.contact': 'Contact — Bluefield Chemicals',
  'seo.route.privacy': 'Privacy & cookies — Bluefield Chemicals',
  'seo.route.login': 'Log in — Bluefield Chemicals',
  'seo.route.leads': 'Leads — Bluefield Chemicals',
  'seo.route.reports': 'Dashboard — Bluefield Chemicals',
  'seo.route.users': 'Users — Bluefield Chemicals',
  'seo.route.console': 'Console — Bluefield Chemicals',
  'seo.route.devices': 'Devices — Bluefield Chemicals',
  'seo.route.account': 'Account — Bluefield Chemicals',
  'home.eyebrow': 'Bluefield Chemicals',
  'home.lead': 'Securing supply. Moving markets. Delivering on every commitment.',
  'home.sub':
    'A petrochemical sourcing and trading partner for companies that cannot afford to run short.',
  'home.cta': 'Talk to our trade desk',
  'home.cta2': 'Request a quote',
  'home.card1.title': 'Supply secured',
  'home.card1.body':
    'Strong sourcing relationships and deep market knowledge let us find product even when supply is tight.',
  'home.card2.title': 'Speed when it counts',
  'home.card2.body':
    'In volatile markets, fast action protects your operation — and we are built to move quickly.',
  'home.card3.title': 'Trade handled',
  'home.card3.body':
    'Logistics, customs, and cross-border compliance, managed end to end so orders arrive complete and on time.',

  'who.title': 'Who We Are',
  'who.p1':
    'Bluefield Chemicals is a petrochemical distribution and trading company. In plain terms, we connect the manufacturers who produce essential industrial chemicals with the businesses that depend on them — and we manage everything in between: sourcing the product, negotiating the price, arranging transport, handling cross-border paperwork, and delivering on schedule.',
  'who.p2':
    'We operate across the United States, Mexico, and South America, with direct access to supply, storage, and cross-border transport. Our focus never changes: secure the product, control the logistics, and deliver on time — even when market conditions make that difficult.',
  'who.p3':
    'When supply tightens, when manufacturers ration how much each customer can buy, and when other suppliers fall short, our clients rely on Bluefield Chemicals to keep their operations moving.',

  'what.title': 'What We Do',
  'what.intro':
    'We make a complex, fast-moving market simple for our clients. Our work falls into three areas:',
  'what.sourcing.title': 'Sourcing & trading',
  'what.sourcing.body':
    'We find and secure the materials our clients need, including products that are hard to obtain or in short supply, and we structure each deal to make commercial sense.',
  'what.logistics.title': 'Logistics & delivery',
  'what.logistics.body':
    'We manage the movement of product from origin to destination — transport, storage, scheduling, and the documentation that crosses borders — so orders arrive complete and on time.',
  'what.expertise.title': 'Market & compliance expertise',
  'what.expertise.body':
    'We track pricing and availability, navigate the regulations that govern chemical trade, and give our clients clear guidance so they can plan with confidence.',

  'where.title': 'Where We Operate',
  'where.intro':
    'We work across three connected markets, with the relationships and infrastructure to source in one region and deliver in another:',
  'where.us.name': 'United States',
  'where.us.desc':
    'Strategic sourcing, export structuring, and close coordination with suppliers.',
  'where.mx.name': 'Mexico',
  'where.mx.desc':
    'Industrial distribution and supply to both private-sector and government buyers.',
  'where.sa.name': 'South America',
  'where.sa.desc': 'Market access, local partnerships, and reliable demand fulfillment.',
  'where.outro':
    'This cross-border reach lets us move quickly and find supply where others cannot.',

  'supply.title': 'What We Supply',
  'supply.intro':
    'We concentrate on the products that industry depends on most — high-demand materials that are critical to day-to-day operations.',
  'supply.core.title': 'Core Petrochemicals',
  'supply.core.btx.name': 'Aromatics (BTX)',
  'supply.core.btx.desc':
    'Benzene, toluene, and xylenes: foundational building blocks behind plastics, synthetic fibers, paints, adhesives, and packaging.',
  'supply.core.olefins.name': 'Olefins & derivatives',
  'supply.core.olefins.desc':
    'Ethylene, propylene, and related products: the raw materials for most plastics, films, and synthetic materials.',
  'supply.core.solvents.name': 'Industrial solvents',
  'supply.core.solvents.desc':
    'Liquids used to dissolve, clean, thin, or carry other substances in manufacturing, from coatings to cleaning processes.',
  'supply.core.fuel.name': 'Fuel components & additives',
  'supply.core.fuel.desc':
    'Blending materials and additives that improve the performance, stability, and quality of fuels.',
  'supply.refining.title': 'Refining & Process Materials',
  'supply.refining.catalysts.name': 'Catalysts & adsorbents',
  'supply.refining.catalysts.desc':
    'Catalysts speed up the chemical reactions at the heart of refining and manufacturing without being used up; adsorbents capture impurities to protect equipment and improve product quality.',
  'supply.refining.hydro.name': 'Hydroprocessing inputs',
  'supply.refining.hydro.desc':
    'Materials that help refiners remove sulfur and other impurities from fuels and feedstocks, meeting clean-fuel standards.',
  'supply.refining.process.name': 'Process optimization chemicals',
  'supply.refining.process.desc':
    'Specialty chemicals that keep industrial processes running efficiently, safely, and reliably.',
  'supply.specialty.title': 'Specialty & Performance Chemicals',
  'supply.specialty.additives.name': 'Industrial-grade additives',
  'supply.specialty.additives.desc':
    'Ingredients that enhance the performance of finished products.',
  'supply.specialty.coatings.name': 'Coatings & treatment solutions',
  'supply.specialty.coatings.desc':
    'Materials that protect, finish, or treat surfaces and equipment.',
  'supply.specialty.custom.name': 'Custom-sourced materials',
  'supply.specialty.custom.desc':
    'Hard-to-find or specification-specific products that we locate and secure on request.',
  'supply.outro':
    'If a material is hard to find, tightly regulated, or in short supply, that is exactly where we add the most value.',

  'partners.title': 'Trusted Supply Relationships',
  'partners.intro':
    'We work with globally recognized manufacturers and technology leaders, which gives our clients dependable access to quality product — even in constrained markets:',
  'partners.basf': 'BASF — One of the world\'s largest chemical producers.',
  'partners.3m': '3M — A global leader in materials science and industrial technology.',
  'partners.honeywell':
    'Honeywell UOP — A leading supplier of refining and petrochemical catalysts, adsorbents, and process technology.',
  'partners.marcopolo':
    'Marco Polo International — A global distributor of plastics and petrochemical products.',
  'partners.outro':
    'We use these relationships to secure supply when it is scarce and to deliver materials that are verified and specification-compliant — meaning every shipment meets the agreed standard.',

  'industries.title': 'Industries We Serve',
  'industries.intro':
    'We supply organizations that simply cannot afford a disruption to their supply:',
  'industries.oilgas': 'Oil & gas operators',
  'industries.refiners': 'Refiners and petrochemical plants',
  'industries.manufacturers': 'Industrial manufacturers',
  'industries.energy': 'Energy and infrastructure projects',
  'industries.government': 'Government and strategic procurement',
  'industries.outro':
    'For these clients, a missed delivery is not an inconvenience — it stops production. That is the standard we hold ourselves to.',

  'logistics.title': 'Logistics & Delivery',
  'logistics.intro':
    'For us, execution is everything. We manage the full chain so our clients don\'t have to:',
  'logistics.formats.title': 'Flexible delivery formats',
  'logistics.formats.desc': 'Bulk, ISO tank, and packaged shipments to match each order.',
  'logistics.terminal.title': 'Terminal and tank-farm coordination',
  'logistics.terminal.desc':
    'Reliable storage and handling at key points along the route.',
  'logistics.compliance.title': 'Cross-border compliance',
  'logistics.compliance.desc':
    'Customs, certifications, and documentation handled correctly the first time.',
  'logistics.terms.title': 'Clear trade terms',
  'logistics.terms.desc':
    'We work to standard international terms (FOB, CIF, and DDP) so every party knows exactly who is responsible for cost, transport, and risk at each stage.',
  'logistics.rapid.title': 'Rapid-response sourcing',
  'logistics.rapid.desc': 'Fast action when timelines are tight and supply is uncertain.',
  'logistics.outro':
    'When a problem comes up in transit, we solve it — and keep our clients informed every step of the way.',
  'logistics.note':
    'FOB (Free On Board) — the buyer takes over once goods are loaded at the origin port. CIF (Cost, Insurance & Freight) — we cover shipping and insurance to the destination port. DDP (Delivered Duty Paid) — we handle everything, including duties, right to the buyer\'s door.',

  'why.title': 'Why Bluefield Chemicals',
  'why.supply.title': 'We secure supply when others cannot.',
  'why.supply.body':
    'Strong sourcing relationships and market knowledge mean we find product in tight markets.',
  'why.speed.title': 'We move quickly.',
  'why.speed.body':
    'In volatile conditions, speed protects our clients — and we are built to act fast.',
  'why.rules.title': 'We understand the rules.',
  'why.rules.body':
    'Chemical trade is heavily regulated; we manage that complexity so our clients don\'t have to.',
  'why.practical.title': 'We deliver practical solutions.',
  'why.practical.body':
    'Our recommendations are commercially sound and ready to act on.',
  'why.protect.title': 'We protect your operations.',
  'why.protect.body':
    'Above all, we keep product flowing so our clients can keep running.',

  'buyers.title': 'For Buyers & Procurement Teams',
  'buyers.intro': 'If you are dealing with any of the following, we should talk:',
  'buyers.pain1': 'Limited allocations or capped order volumes',
  'buyers.pain2': 'Suppliers who don\'t deliver as promised',
  'buyers.pain3': 'Unpredictable pricing',
  'buyers.pain4': 'Urgent or hard-to-source requirements',
  'buyers.outro':
    'Bring us the challenge. We will tell you honestly what we can secure, at what price, and how quickly.',
  'buyers.cta': 'Contact our procurement team',

  'info.title': 'Contact',
  'info.intro': 'We\'d be glad to hear from you. Reach the right team directly:',
  'info.sales': 'Sales',
  'info.procurement': 'Procurement',
  'info.trading': 'Trade desk',
  'info.phone': 'Phone',
  'info.offices': 'Office',
  'info.officesValue': '2451 NW 109th Ave, Unit 12, Miami, FL 33172, USA',

  'closing.statement':
    'Bluefield Chemicals works where supply is scarce and execution matters most. We don\'t compete on promises — we compete on delivery.',

  'nav.about': 'About',
  'nav.supply': 'Supply',
  'nav.why': 'Why BFC',
  'nav.home': 'Home',
  'nav.contact': 'Contact',
  'nav.me': 'Me',
  'nav.devices': 'Devices',
  'nav.passkeys': 'Passkeys',
  'nav.admin': 'Admin',
  'nav.users': 'Users',
  'nav.capture': 'Capture lead',
  'nav.myLeads': 'My leads',
  'nav.leadsInbox': 'Leads inbox',
  'nav.inbox': 'Inbox',
  'nav.leads': 'Leads',
  'nav.login': 'Log in',
  'nav.account': 'Account',
  'nav.console': 'Console',
  'nav.reports': 'Dashboard',
  'contact.title': 'Talk to our trade desk',
  'contact.sub':
    'Bring us the challenge — what you need to source, and how fast. We will tell you honestly what we can secure, at what price, and how quickly.',
  'contact.field.name': 'Name',
  'contact.field.email': 'Work email',
  'contact.field.company': 'Company (optional)',
  'contact.field.need': 'What do you need?',
  'contact.field.message': 'Message (optional)',
  'contact.field.reason': 'Reason for contact',
  'contact.consent':
    'I consent to Bluefield Chemicals contacting me about this enquiry and related products. I can opt out at any time.',
  'contact.submit': 'Send message',
  'contact.sending': 'Sending…',
  'contact.validation': 'Please complete the required fields.',
  'contact.requiredHint': '* Required',
  'contact.err.required': 'Please fill out this field.',
  'contact.err.email': 'Enter a valid email address.',
  'contact.err.consent': 'Please give your consent to continue.',
  'contact.error.badRequest': 'Please check the form and try again.',
  'contact.error.forbidden': 'We couldn’t verify this request. Reload the page and try again.',
  'contact.error.rateLimited': 'Too many requests — give it a minute, then try again.',
  'contact.error.server': 'Something went wrong on our end. Please try again.',
  'contact.error.network': 'Could not reach the server. Check your connection.',
  'contact.success.title': 'Message sent',
  'contact.success.lead': 'Thanks for reaching out. A member of our team will be in touch shortly.',
  'contact.consentLink': 'Read our privacy notice',
  'capture.title': 'Capture a lead',
  'capture.sub': 'Log a prospect in seconds. It saves as a lead under your name.',
  'capture.field.firstName': 'First name',
  'capture.field.lastName': 'Last name',
  'capture.field.email': 'Email',
  'capture.field.cell': 'Cell (optional)',
  'capture.field.company': 'Company (optional)',
  'capture.field.product': 'Product interest (optional)',
  'capture.field.amount': 'Amount / quantity (optional)',
  'capture.field.notes': 'Notes (optional)',
  'capture.consent': 'The prospect consented to being contacted about this and related products.',
  'capture.consentLink': 'Read our privacy notice',
  'capture.submit': 'Save lead',
  'capture.sending': 'Saving…',
  'capture.validation': 'Please complete the required fields.',
  'capture.error.badRequest': 'Please check the form and try again.',
  'capture.error.forbidden': 'Your account cannot capture leads.',
  'capture.error.server': 'Something went wrong on our end. Please try again.',
  'capture.error.network': 'Could not reach the server. The lead was not saved.',
  'capture.success.title': 'Lead saved',
  'capture.success.lead': 'The lead is now under your name. Capture another whenever you are ready.',
  'capture.offline.title': 'Saved on your device',
  'capture.offline.lead':
    'No signal right now — this lead is queued and uploads automatically when you are back online.',
  'capture.pending': 'waiting to sync',
  'capture.another': 'Capture another',
  'leads.title': 'My leads',
  'leads.sub': 'The leads you’ve captured, newest first.',
  'leads.search': 'Search your leads',
  'leads.count.one': 'lead',
  'leads.count.other': 'leads',
  'leads.loading': 'Loading your leads…',
  'leads.empty': 'You haven’t captured any leads yet. Capture your first from the field.',
  'leads.noMatch': 'No leads match your search.',
  'leads.error': 'Could not load your leads. Check your connection.',
  'leads.retry': 'Try again',
  'leads.more': 'Load more',
  'leads.col.name': 'Name',
  'leads.col.company': 'Company',
  'leads.col.product': 'Product',
  'leads.col.source': 'Source',
  'leads.col.status': 'Status',
  'leads.col.assignee': 'Assignee',
  'leads.col.captured': 'Captured',
  'leads.status.new': 'New',
  'leads.status.contacted': 'Contacted',
  'leads.status.qualified': 'Qualified',
  'leads.status.quoted': 'Quoted',
  'leads.status.won': 'Won',
  'leads.status.lost': 'Lost',
  'leads.source.web': 'Web',
  'leads.source.field': 'Field',
  'leads.inbox.title': 'Leads inbox',
  'leads.inbox.sub': 'Every lead, web and field, newest first.',
  'leads.inbox.search': 'Search leads',
  'leads.inbox.loading': 'Loading leads…',
  'leads.inbox.empty': 'No leads yet. Web-form and field-captured leads land here.',
  'leads.inbox.emptyFiltered': 'No leads match these filters.',
  'leads.inbox.error': 'Could not load leads. Check your connection.',
  'leads.filter.source': 'Source',
  'leads.filter.status': 'Status',
  'leads.filter.assignee': 'Assignee',
  'leads.filter.unassigned': 'Unassigned',
  'leads.filter.all': 'All',
  'leads.export.action': 'Export CSV',
  'leads.export.busy': 'Exporting…',
  'leads.export.error': 'Could not export leads. Try again.',
  'leads.export.aria': 'Export the filtered leads as a CSV file',
  'leads.export.none': 'No leads match the current filters — nothing to export.',
  'leads.detail.open': 'View full record',
  'leads.detail.back': 'Back to leads',
  'leads.detail.loading': 'Loading lead…',
  'leads.detail.error': 'Could not load this lead. Check your connection.',
  'leads.detail.notFound': 'This lead isn’t available.',
  'leads.detail.record': 'Record',
  'leads.detail.activity': 'Activity',
  'leads.detail.amount': 'Amount',
  'leads.detail.email': 'Email',
  'leads.detail.phone': 'Phone',
  'leads.detail.owner': 'Assignee',
  'leads.detail.capturedBy': 'Captured by',
  'leads.detail.message': 'Message',
  'leads.detail.notes': 'Capture notes',
  'leads.detail.consent': 'Consent',
  'leads.detail.consentYes': 'Given',
  'leads.detail.consentNo': 'Not given',
  'leads.detail.created': 'Created',
  'leads.detail.lastActivity': 'Last activity',
  'leads.detail.unassigned': 'Unassigned',
  'leads.activity.empty': 'No activity yet. Add the first note.',
  'leads.activity.created': 'Lead created',
  'leads.activity.note': 'Note',
  'leads.activity.statusChanged': 'Stage changed',
  'leads.activity.assigned': 'Assigned',
  'leads.note.label': 'Add a note',
  'leads.note.placeholder': 'Write an internal note about this lead…',
  'leads.note.add': 'Add note',
  'leads.note.adding': 'Adding…',
  'leads.note.empty': 'Enter a note first.',
  'leads.note.error': 'Could not add the note. Try again.',
  'leads.status.change': 'Move stage',
  'leads.status.updating': 'Updating…',
  'leads.status.error': 'Could not move the lead. Try again.',
  'leads.status.conflict': 'This lead’s stage changed elsewhere; it has been reloaded.',
  'leads.assign.label': 'Assign to (rep email)',
  'leads.assign.placeholder': 'rep@bluefieldchem.com',
  'leads.assign.action': 'Assign',
  'leads.assign.empty': 'Enter the rep’s email.',
  'leads.assign.assigning': 'Assigning…',
  'leads.assign.done': 'Assigned.',
  'leads.assign.conflict': 'This lead was reassigned elsewhere; it has been reloaded.',
  'leads.assign.invalid': 'That person can’t be assigned leads.',
  'leads.assign.error': 'Could not assign the lead. Try again.',
  'footer.privacy': 'Privacy & cookies',
  'privacy.title': 'Privacy & cookies',
  'privacy.updated': 'Last updated: July 9, 2026',
  'privacy.intro':
    'Bluefield Chemicals respects the people behind every enquiry. This page explains, in plain language, what personal data this website collects, why we collect it, and the choices you have.',
  'privacy.who.title': 'Who we are',
  'privacy.who.body':
    'Bluefield Chemicals is a petrochemical sourcing and trading company. For the personal data collected through this website, Bluefield Chemicals is the data controller.',
  'privacy.collect.title': 'What we collect',
  'privacy.collect.body':
    'When you contact our trade desk we ask for your name, work email, an optional phone number, a subject, and your message, together with a record of the consent you give. Our infrastructure also keeps standard security logs — such as the network address a request came from — to protect the service against abuse.',
  'privacy.use.title': 'How we use it',
  'privacy.use.body':
    'We use your details to respond to your enquiry and follow up on the business you asked about. With your consent we may also contact you about related products — you can opt out at any time. Security logs are used only to block abusive traffic. We never sell your data and run no third-party advertising.',
  'privacy.store.title': 'Where it lives',
  'privacy.store.body':
    'Your data is stored in our database on Google Cloud (United States) and moves over encrypted connections end to end. Notification emails are delivered through our email provider. Access is restricted to the Bluefield Chemicals team.',
  'privacy.retain.title': 'How long we keep it',
  'privacy.retain.body':
    'We keep your data while we work on your enquiry and while a business relationship stays active. After an enquiry is closed we keep it for up to five years from your last activity, then review and remove it. Ask us to erase it and we will do so straight away.',
  'privacy.rights.title': 'Your rights',
  'privacy.rights.body':
    'You may ask at any time to see, correct, export, or erase the data we hold about you, or to stop marketing follow-up. There is no form and no fee — tell us what you need and we will act on it.',
  'privacy.cookies.title': 'Cookies & local storage',
  'privacy.cookies.body':
    'The public site sets no advertising or analytics cookies and uses no third-party trackers. Your language choice is kept locally in your browser, and the site uses strictly necessary cookies for security — protecting forms against forgery and keeping staff sign-ins working. That is all.',
  'privacy.requests.title': 'Privacy requests',
  'privacy.requests.body':
    'To see, correct, erase, or export your data — or opt out of marketing — reach us through the contact form and put "privacy request" in the subject. We respond promptly.',
  'privacy.requests.cta': 'Go to the contact form',
  'locale.switcher.label': 'Language',
  'locale.name.en': 'English',
  'locale.name.es': 'Español',
  'locale.name.pt': 'Português',
  'admin.users.title': 'User management',
  'admin.users.sub': 'Create an account and assign a role. New users sign in with a magic link — no password to set.',
  'admin.users.field.email': 'Email',
  'admin.users.field.name': 'Name',
  'admin.users.field.role': 'Role',
  'admin.users.role.placeholder': 'Select a role…',
  'admin.users.submit': 'Create user',
  'admin.users.success': 'User created. They can now sign in with a magic link.',
  'admin.users.error.email': 'Enter a valid email address.',
  'admin.users.error.exists': 'A user with that email already exists.',
  'admin.users.error.role': 'Choose a valid role.',
  'admin.users.error.badRequest': 'Please check the details and try again.',
  'admin.users.error.forbidden': 'You do not have permission to do this.',
  'admin.users.error.server': 'Something went wrong. Please try again.',
  'admin.users.error.network': 'Network error. Please check your connection and try again.',
  'admin.role.admin': 'Admin',
  'admin.role.manager': 'Manager',
  'admin.role.sales': 'Sales',
  'admin.role.viewer': 'Viewer',
  'admin.desktop.title': 'Use a computer',
  'admin.desktop.copy': 'User management is available on a desktop or laptop. Please sign in from a computer to continue.',
  'console.title': 'Visibility console',
  'console.sub': 'System status, security, activity, compliance, and configuration — a read-only view of the platform.',
  'console.loading': 'Loading…',
  'console.lens.status': 'System status',
  'console.lens.security': 'Security',
  'console.lens.activity': 'Activity',
  'console.lens.compliance': 'Compliance',
  'console.lens.config': 'Configuration',
  'console.desktop.title': 'Use a computer',
  'console.desktop.copy': 'The visibility console is available on a desktop or laptop. Please sign in from a computer to continue.',
  'console.error': 'This lens could not be loaded. Please try again.',
  'console.retry': 'Try again',
  'console.status.error': 'Live status could not be loaded. Please try again.',
  'console.status.retry': 'Retry',
  'console.status.deploy.title': 'Deployment',
  'console.status.deploy.version': 'Version',
  'console.status.deploy.commit': 'Commit',
  'console.status.deploy.built': 'Built',
  'console.status.db.title': 'Database',
  'console.status.db.ok': 'Healthy',
  'console.status.db.down': 'Unreachable',
  'console.status.db.latency': 'Round-trip',
  'console.status.traffic.title': 'Traffic',
  'console.status.traffic.window': 'Last 24 hours',
  'console.status.traffic.unavailable': 'Traffic metrics are temporarily unavailable.',
  'console.status.traffic.requests': 'Requests',
  'console.status.traffic.errorRate': 'Error rate',
  'console.status.traffic.clientErrors': 'Client errors (4xx)',
  'console.status.traffic.serverErrors': 'Server errors (5xx)',
  'console.status.traffic.topEndpoints': 'Busiest endpoints',
  'console.status.traffic.noEndpoints': 'No requests recorded in this window yet.',
  'console.security.error': 'Live security data could not be loaded. Please try again.',
  'console.security.denials.title': 'Denial posture',
  'console.security.denials.window': 'Last 24 hours',
  'console.security.denials.unavailable': 'Denial metrics are temporarily unavailable.',
  'console.security.denials.total': 'Requests denied',
  'console.security.denials.byReason': 'By reason',
  'console.security.denials.topSources': 'Busiest blocked sources',
  'console.security.denials.none': 'No requests were denied in this window.',
  'console.security.blocks.title': 'Blocked IP addresses',
  'console.security.blocks.none': 'No IP addresses are currently blocked.',
  'console.security.blocks.reason': 'Reason',
  'console.security.blocks.blockedBy': 'Blocked by',
  'console.security.blocks.blockedAt': 'Blocked',
  'console.security.blocks.expires': 'Expires',
  'console.security.blocks.permanent': 'Permanent',
  'console.security.blocks.unblock': 'Lift block',
  'console.security.tiers.title': 'Rate-limit tiers',
  'console.security.tiers.none': 'No rate-limit tiers are configured.',
  'console.security.tiers.tier': 'Tier',
  'console.security.tiers.limit': 'Limit',
  'console.security.unblock.title': 'Lift this block',
  'console.security.unblock.prompt': 'Why are you lifting this block? This is recorded in the audit log.',
  'console.security.unblock.reason': 'Reason',
  'console.security.unblock.placeholder': 'e.g. confirmed false positive from a partner NAT',
  'console.security.unblock.confirm': 'Lift block',
  'console.security.unblock.cancel': 'Cancel',
  'console.security.unblock.submitting': 'Lifting…',
  'console.security.unblock.error': 'The block could not be lifted. Please try again.',
  'console.security.unblock.required': 'A reason is required.',
  'console.activity.error': 'Activity could not be loaded. Please try again.',
  'console.activity.audit.title': 'Audit trail',
  'console.activity.audit.sub': 'Every request that reached a handler — who did what, newest first.',
  'console.activity.audit.empty': 'No matching audit entries.',
  'console.activity.filter.outcome': 'Outcome',
  'console.activity.filter.all': 'All outcomes',
  'console.activity.outcome.ok': 'OK',
  'console.activity.outcome.client_error': 'Client error',
  'console.activity.outcome.server_error': 'Server error',
  'console.activity.filter.actor': 'Actor',
  'console.activity.filter.clear': 'Clear filter',
  'console.activity.col.time': 'Time',
  'console.activity.col.actor': 'Actor',
  'console.activity.col.action': 'Action',
  'console.activity.col.source': 'Source',
  'console.activity.col.outcome': 'Outcome',
  'console.activity.actor.none': 'Unauthenticated',
  'console.activity.more': 'Load more',
  'console.activity.signins.title': 'Sign-in events',
  'console.activity.signins.sub': 'Session issue, refresh, and revoke — newest first.',
  'console.activity.signins.empty': 'No sign-in events yet.',
  'console.activity.event.issued': 'Signed in',
  'console.activity.event.refreshed': 'Refreshed',
  'console.activity.event.revoked': 'Revoked',
  'console.activity.event.revoked_all': 'Revoked all',
  'console.compliance.error': 'The compliance summary could not be loaded. Please try again.',
  'console.compliance.summary.title': 'Access-event summary',
  'console.compliance.summary.sub': 'Data-subject access events over the last 30 days.',
  'console.compliance.total': 'Total access events',
  'console.compliance.action.export': 'Exports',
  'console.compliance.action.export_csv': 'DSAR CSV exports',
  'console.compliance.action.rectify': 'Rectifications',
  'console.compliance.action.erase': 'Erasures',
  'console.compliance.recent.title': 'Recent access events',
  'console.compliance.recent.empty': 'No data-subject access events in this window yet.',
  'console.compliance.recent.col.time': 'Time',
  'console.compliance.recent.col.actor': 'Actor',
  'console.compliance.recent.col.action': 'Action',
  'console.compliance.recent.col.affected': 'Affected records',
  'console.compliance.recent.actor.none': 'System',
  'console.compliance.export.title': 'DSAR export',
  'console.compliance.export.sub': 'Download everything held about a person as a CSV report to hand over.',
  'console.compliance.export.emailLabel': 'Subject email',
  'console.compliance.export.emailPlaceholder': 'person@example.com',
  'console.compliance.export.button': 'Export CSV',
  'console.compliance.export.busy': 'Preparing…',
  'console.compliance.export.success': 'Your download has started.',
  'console.compliance.export.error': 'The export could not be generated. Please try again.',
  'console.compliance.export.invalidEmail': 'Enter a valid email address.',
  'auth.email': 'Email',
  'auth.emailPlaceholder': 'name@example.com',
  'auth.sending': 'Sending…',
  'auth.cancel': 'Cancel',
  'auth.backToSignIn': 'Back to sign in',
  'auth.error.network': 'Could not reach the server. Check your connection.',
  'auth.error.rateLimited': 'Too many attempts — give it a minute.',
  'auth.error.server': 'Something went wrong on our end. Please try again.',
  'login.title': 'Sign in',
  'login.sub': 'Enter your email — we’ll send you a magic link.',
  'login.submit': 'Send magic link',
  'login.sent': 'Check your inbox',
  'login.success': 'If this email is registered, a link is on its way to your inbox.',
  'login.footnote': 'We’ll never share your email.',
  'login.or': 'or',
  'login.oauth.apple': 'Continue with Apple',
  'login.oauth.google': 'Continue with Google',
  'login.recoveryLink': 'Lost your passkey?',
  'login.error.captcha': 'We couldn’t verify this request. Reload the page and try again.',
  'login.oauthError.denied': 'Sign-in was cancelled. Try again, or use a magic link instead.',
  'login.oauthError.emailMissing':
    'Your provider didn’t share an email address, which we need to sign you in. Try a magic link instead.',
  'login.oauthError.emailUnverified':
    'Your Google email isn’t verified yet. Verify it with Google, or use a magic link.',
  'login.oauthError.deviceLimit':
    'You already have 3 active sessions. Revoke one under Settings → Devices to sign in here.',
  'login.oauthError.stateInvalid': 'That sign-in attempt expired or was invalid. Please try again.',
  'login.oauthError.generic': 'Something went wrong signing in. Try again, or use a magic link.',
  'recovery.title': 'Lost your passkey?',
  'recovery.sub':
    'We’ll help you get back in. For your security, an administrator verifies your identity before your passkey is reset.',
  'recovery.step1.title': 'Request recovery',
  'recovery.step1.detail': 'Enter your email and we’ll alert an administrator.',
  'recovery.step2.title': 'Verify your identity',
  'recovery.step2.detail': 'An admin contacts you on a registered channel to confirm it’s really you.',
  'recovery.step3.title': 'Sign in & set up again',
  'recovery.step3.detail': 'Your passkey is reset, so you can enroll a new one next time you sign in.',
  'recovery.submit': 'Request recovery',
  'recovery.success.title': 'Request received',
  'recovery.success.lead':
    'If an account matches that email, an administrator has been notified to verify your identity and help you recover access.',
  'recovery.success.note':
    'You can close this page — the team will reach out on a channel you’ve registered with us.',
  'recovery.error.rateLimited': 'Too many requests — give it a minute, then try again.',
  'verify.title': 'Signing you in…',
  'verify.sub': 'One moment while we verify your link.',
  'verify.deviceLimit.title': 'Device limit reached',
  'verify.deviceLimit.capN': 'the maximum of {max} devices',
  'verify.deviceLimit.capUnknown': 'the maximum number of devices',
  'verify.deviceLimit.message':
    'You\'re signed in on {cap}. On a device you\'re already signed in on, open Settings → Devices, sign one out, then request a new sign-in link.',
  'verify.deviceLimit.cta': 'Request a new sign-in link',
  'verify.invalid.title': 'Sign-in link invalid',
  'verify.invalid.expired': 'This link has expired or already been used. Request a fresh one.',
  'verify.invalid.generic': 'We could not verify the link. Try requesting a fresh one.',
  'verify.connection.title': 'Connection problem',
  'verify.connection.message':
    'We couldn’t reach the server to verify your link. It may still be valid — check your connection and try again.',
  'verify.connection.retry': 'Try again',
  'sessions.title': 'Devices & sessions',
  'sessions.sub':
    'These are the devices currently signed in to your account. Revoke any you don\'t recognise.',
  'sessions.loading': 'Loading your sessions…',
  'sessions.empty': 'No active sessions found.',
  'sessions.retry': 'Try again',
  'sessions.error.load': 'Could not load your sessions. Check your connection.',
  'sessions.error.alreadyOut': 'That session was already signed out.',
  'sessions.error.currentDevice': 'That’s the device you’re using now — use “Sign out this device” instead.',
  'sessions.error.generic': 'Something went wrong. Please try again.',
  'sessions.thisDevice': 'This device',
  'sessions.col.device': 'Device',
  'sessions.col.lastUsed': 'Last used',
  'sessions.col.ip': 'IP address',
  'sessions.col.signedIn': 'Signed in',
  'sessions.actions': 'Actions',
  'sessions.reported': 'Reported — will be signed out and wiped when it next connects',
  'sessions.signOutThis': 'Sign out this device',
  'sessions.revoke': 'Revoke',
  'sessions.revokeAria': 'Revoke {device}',
  'sessions.reportBtn': 'This wasn\'t me',
  'sessions.reportAria': 'Report {device} as not mine',
  'sessions.signOutOthers': 'Sign out all other devices',
  'sessions.unknown': 'Unknown',
  'sessions.unknownDevice': 'Unknown device',
  'sessions.lastUsed.activeNow': 'Active now',
  'sessions.lastUsed.minuteOne': '1 minute ago',
  'sessions.lastUsed.minuteOther': '{n} minutes ago',
  'sessions.lastUsed.hourOne': '1 hour ago',
  'sessions.lastUsed.hourOther': '{n} hours ago',
  'sessions.lastUsed.yesterday': 'Yesterday',
  'sessions.lastUsed.dayOther': '{n} days ago',
  'sessions.confirmSignOut.title': 'Sign out this device?',
  'sessions.confirmSignOut.message':
    'You\'ll be signed out here and returned to the sign-in screen. Your other devices stay signed in.',
  'sessions.confirmSignOut.confirm': 'Sign out',
  'sessions.confirmSignOut.cancel': 'Stay signed in',
  'sessions.confirmReport.title': 'Report this device?',
  'sessions.confirmReport.message':
    'We\'ll sign this device out and erase anything it saved offline the next time it connects, and it\'ll have to set up a passkey again. Only do this if you don\'t recognise it.',
  'sessions.confirmReport.confirm': 'Report device',
  'sessions.confirmSignOutOthers.title': 'Sign out all other devices?',
  'sessions.confirmSignOutOthers.messageOne':
    'Your one other device will be signed out. This device stays signed in.',
  'sessions.confirmSignOutOthers.messageOther':
    'All {n} of your other devices will be signed out. This device stays signed in.',
  'sessions.confirmSignOutOthers.confirm': 'Sign out others',
  'sessions.status.signedOut': 'Signed out {device}.',
  'sessions.status.reported':
    'Reported {device}. It\'ll be signed out and its offline data wiped the next time it connects.',
  'sessions.status.signedOutOthers.one': 'Signed out {n} other device.',
  'sessions.status.signedOutOthers.other': 'Signed out {n} other devices.',
  'sessions.status.signedOutPartial.one': 'Signed out {n} device; {failed} could not be signed out.',
  'sessions.status.signedOutPartial.other': 'Signed out {n} devices; {failed} could not be signed out.',
  'passkeys.title': 'Passkeys',
  'passkeys.sub':
    'Passkeys protect sensitive actions like approvals and reports. Add a backup so you’re never locked out, and remove any you no longer use.',
  'passkeys.loading': 'Loading your passkeys…',
  'passkeys.unsupported': 'This browser doesn’t support passkeys. Use a recent Chrome, Safari, or Firefox.',
  'passkeys.retry': 'Try again',
  'passkeys.error.load': 'Could not load your passkeys. Check your connection.',
  'passkeys.error.notAllowed':
    'Couldn’t add the passkey — it may have been cancelled or timed out, or that authenticator already has a passkey for your account.',
  'passkeys.error.invalidState': 'That authenticator already has a passkey on your account.',
  'passkeys.error.setup': 'Your authenticator couldn’t complete setup. Try again.',
  'passkeys.error.forbidden': 'Passkeys are available to office roles on a desktop.',
  'passkeys.error.generic': 'Could not set up the passkey. Try again.',
  'passkeys.error.remove': 'Could not remove the passkey. Try again.',
  'passkeys.empty': 'You haven’t set up a passkey yet.',
  'passkeys.label': 'Passkey',
  'passkeys.labelLower': 'passkey',
  'passkeys.unknown': 'Unknown',
  'passkeys.never': 'Never',
  'passkeys.meta.added': 'Added',
  'passkeys.meta.lastUsed': 'Last used',
  'passkeys.add.first': 'Set up a passkey',
  'passkeys.add.another': 'Add another passkey',
  'passkeys.testStepUp': 'Test step-up',
  'passkeys.verify.success': 'Passkey verified — step-up works.',
  'passkeys.verify.cancelled': 'Passkey check cancelled or timed out.',
  'passkeys.verify.failed': 'Step-up verification failed. Try again.',
  'passkeys.remove': 'Remove',
  'passkeys.removeAria': 'Remove {label}',
  'passkeys.added': 'Passkey added.',
  'passkeys.removed': 'Passkey removed.',
  'passkeys.removed.already': 'That passkey was already removed.',
  'passkeys.confirmRemove.title': 'Remove this passkey?',
  'passkeys.confirmRemove.message':
    'This passkey will no longer work for verifying sensitive actions. Your other passkeys keep working.',
  'passkeys.confirmRemove.messageLast':
    'This is your only passkey. After removing it you’ll be asked to set one up again next time you sign in on a desktop.',
  'passkeys.confirmRemove.cancel': 'Keep',
  'passkeys.info.sub':
    'Passkeys are set up and managed on a desktop computer — sensitive actions that need them are desktop-only. Open the portal on your desktop to add or remove a passkey.',
  'me.account': 'Account',
  'me.appearance': 'Appearance',
  'me.signOut': 'Sign out',
  'me.theme.label': 'Theme',
  'me.theme.auto': 'Auto',
  'me.theme.light': 'Light',
  'me.theme.dark': 'Dark',
  'me.signedIn.phone': 'Signed in on this phone.',
  'me.signedIn.tablet': 'Signed in on this tablet.',
  'me.signedIn.desktop': 'Signed in on this computer.',
  'me.signedIn.generic': 'Signed in.',
  'me.confirmSignOut.title': 'Sign out?',
  'me.confirmSignOut.message': 'You will need a new magic link to sign back in on this device.',
  'error.forbidden.title': 'You don\'t have access to this',
  'error.forbidden.copy':
    'This part of the portal is limited to other roles. If you think you should have access, ask your administrator.',
  'error.notFound.title': 'We can\'t find that page',
  'error.notFound.copy': 'The page you\'re looking for doesn\'t exist, or it may have moved.',
  'error.offline.title': 'You\'re offline',
  'error.offline.copy': 'We can\'t reach the portal right now. Check your connection and try again.',
  'error.generic.title': 'Something went wrong',
  'error.generic.copy': 'We hit an unexpected problem. Please try again in a moment.',
  'error.action.home': 'Back to home',
  'error.action.retry': 'Try again',
  'passkey.desktop.title': 'Desktop required',
  'passkey.desktop.message':
    'Sensitive operations need a desktop signed in with a passkey. Switch to your desktop to continue.',
  'passkey.desktop.dismiss': 'OK',
  'passkey.verify.title': 'Verify it’s you',
  'passkey.verify.message':
    'This action needs a passkey check. Use your fingerprint, face, or security key to continue.',
  'passkey.verify.action': 'Use passkey',
  'passkey.verify.progress': 'Waiting for your passkey…',
  'passkey.enroll.title': 'Set up a passkey',
  'passkey.enroll.message':
    'Your role can approve certifications and run reports. Add a passkey so those actions stay protected — it takes a few seconds.',
  'passkey.enroll.action': 'Set up passkey',
  'passkey.enroll.progress': 'Setting up your passkey…',
  'passkey.enroll.cancel': 'Not now',
  'passkey.error.cancelled': 'Passkey prompt was cancelled or timed out. Try again.',
  'passkey.error.exists': 'This device already has a passkey for your account.',
  'passkey.error.authenticator': 'Your authenticator couldn’t complete the request. Try again.',
  'passkey.error.verifyFailed': 'Verification failed. Try again.',
  'passkey.error.generic': 'Something went wrong. Try again.',
  'health.status': 'System status',
  'health.checking': 'Checking…',
  'health.online': 'Online',
  'health.degraded': 'Degraded',
  'health.offline': 'Offline',
  'health.detail': 'Couchbase {status} · {latency} ms',
  'build.builtAt': 'Built at {time}',
  // Shared component chrome — data table, form, confirm dialog, nav (#154)
  'table.aria': '{title} table',
  'table.hint': '↑ ↓ move · Enter open · / search',
  'table.hint.menu': 'right-click for actions',
  'table.filter': 'Filter',
  'table.search': 'Search',
  'table.empty': 'No matches.',
  'table.detailEmpty': 'Select a row to see details.',
  'table.unit.one': 'row',
  'table.unit.other': 'rows',
  'form.save': 'Save',
  'form.select': 'Select…',
  'form.required': '{label} is required.',
  'confirm.cancel': 'Cancel',
  'confirm.confirm': 'Confirm',
  'nav.primaryAria': 'Primary',
  'nav.skipToContent': 'Skip to content',
};

export { en };

// ES and PT-BR catalogs live in messages-es.ts and messages-pt-BR.ts
// (lazy-loaded by locale.ts for code-splitting — keeps the main bundle under 30kB).

// CATALOGS is available for test-time key-parity checks only. It is NOT
// imported by production code (locale.ts uses dynamic import() instead).
const loadEs = (): Promise<Messages> => import('./messages-es').then((m) => m.default);
const loadPtBR = (): Promise<Messages> => import('./messages-pt-BR').then((m) => m.default);
export { loadEs as _testLoadEs, loadPtBR as _testLoadPtBR };

export type MsgKey = keyof Messages;
